import { Banknote, Building2, CheckCircle2, CreditCard, Printer, Tag } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { DocumentButton } from "@/components/DocumentButton";
import { MoneyText } from "@/components/MoneyText";
import { LabelPrintModal } from "@/features/settings/LabelPrintModal";
import { call } from "@/lib/api";
import { errorText } from "@/lib/errors";
import type { Buylist } from "@/lib/types";

type PrintState = "idle" | "printing" | "printed" | "failed";

/** The 買取明細書 (server bytes) printed twice: the seller's copy and the shop's copy. */
export async function printKaitori(
  services: Pick<ReturnType<typeof usePos>, "api" | "printer">,
  intake: Pick<Buylist, "id" | "reference">,
  copies: string[],
): Promise<boolean> {
  const quote = await call(() =>
    services.api.GET("/v1/pos/buylist-intakes/{intake_id}/quote", {
      params: {
        path: { intake_id: intake.id },
        query: { format: "escpos", width: services.printer.config.paperWidth },
      },
    }),
  );
  if (!quote.data) {
    return false;
  }
  let ok = true;
  for (const copy of copies) {
    const job = await services.printer.queue.submit({
      label: `${intake.reference} ${copy}`,
      receiptNo: intake.reference,
      data: quote.data,
    });
    ok = ok && job.status === "done";
  }
  return ok;
}

/**
 * 完了 of a purchase (T07-31): how the seller was paid (cash — the drawer opens, the amount to
 * hand over; store credit issued; bank transfer — the back office pays), the 買取明細書 printed twice
 * (お客様控え / 店控え, 「再印刷」), the price labels of the cards now in stock, and 「次の買取」.
 */
export function BuylistDone({ intake, onNext }: { intake: Buylist; onNext: () => void }) {
  const { t } = useTranslation();
  const services = usePos();
  const { printer } = services;
  const [print, setPrint] = useState<PrintState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [labels, setLabels] = useState(false);
  const started = useRef(false);

  const doPrint = async () => {
    setPrint("printing");
    setError(null);
    try {
      const ok = await printKaitori(services, intake, [
        t("buylist.copy_customer"),
        t("buylist.copy_shop"),
      ]);
      setPrint(ok ? "printed" : "failed");
    } catch (err) {
      setPrint("failed");
      setError(errorText(err, t));
    }
  };

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    if (intake.openDrawer && intake.payout.method === "cash" && printer.config.drawer) {
      void printer.queue.openDrawer();
    }
    void doPrint();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the purchase is done
  }, []);

  const method = intake.payout.method;
  const paid = intake.payout.amount ?? intake.totalOffer;
  const Icon =
    method === "store_credit" ? CreditCard : method === "bank_transfer" ? Building2 : Banknote;

  return (
    <section className="flex h-full flex-col items-center justify-center gap-5 p-6 text-center">
      <CheckCircle2 aria-hidden className="size-16 text-pos-ok" />
      <h1 className="text-3xl font-semibold">{t("buylist.done_title")}</h1>
      <p className="text-lg text-muted-foreground">{intake.reference}</p>
      <p className="flex items-center gap-3 text-2xl">
        <Icon aria-hidden className="size-8" />
        {method === "cash"
          ? t("buylist.done_cash")
          : method === "store_credit"
            ? t("buylist.done_credit")
            : t("buylist.done_bank")}
        <MoneyText amount={paid} size="xl" />
      </p>
      {method === "cash" && intake.openDrawer ? <p>{t("buylist.drawer_opened")}</p> : null}
      {method === "bank_transfer" ? (
        <p className="max-w-xl">{t("buylist.done_bank_note")}</p>
      ) : null}
      <p role="status" className="text-lg">
        {print === "printing"
          ? t("buylist.printing")
          : print === "printed"
            ? t("buylist.printed_two")
            : print === "failed"
              ? (error ?? t("buylist.print_failed"))
              : null}
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={() => void doPrint()}
          disabled={print === "printing"}
          className="flex min-h-pos-touch items-center gap-2 rounded-md border px-6 text-lg disabled:opacity-50"
        >
          <Printer aria-hidden className="size-5" />
          {t("buylist.reprint")}
        </button>
        <button
          type="button"
          onClick={() => setLabels(true)}
          className="flex min-h-pos-touch items-center gap-2 rounded-md border px-6 text-lg"
        >
          <Tag aria-hidden className="size-5" />
          {t("buylist.print_labels")}
        </button>
        <button
          type="button"
          onClick={onNext}
          className="min-h-pos-touch rounded-md bg-primary px-8 text-xl font-semibold text-primary-foreground"
        >
          {t("buylist.next")}
        </button>
      </div>
      <div
        role="group"
        className="flex flex-wrap justify-center gap-3"
        aria-label={t("buylist.documents")}
      >
        <StatementButton intakeId={intake.id} kind="kaitori" />
        <StatementButton intakeId={intake.id} kind="supplier" />
      </div>
      <LabelPrintModal
        open={labels}
        onClose={() => setLabels(false)}
        source={{ buylistIntakeId: intake.id }}
        title={t("buylist.labels_title", { count: intake.lines.length })}
      />
    </section>
  );
}

type StatementKind = "kaitori" | "supplier";

/**
 * media-service's PDF of the purchase (T07-41/42): 買取明細書, and 仕入明細書 for a seller with a
 * T-number (none for a private seller — 古物商特例 — so no button). The state follows the hub
 * (`pos.document.changed` → `buylist_intake` → the `pos-buylist` queries — R21); 「作成中…」 until
 * media-service made it (a PDF it could not make at the counter is asked again every 5 minutes).
 * Reading / refetching the state never makes a PDF link: media records every link as a 電帳法
 * download, so the link is asked only when the button is pressed (`POST …/link`).
 */
function StatementButton({ intakeId, kind }: { intakeId: string; kind: StatementKind }) {
  const { t } = useTranslation();
  const { api } = usePos();
  const state = useQuery({
    queryKey: ["pos-buylist", "detail", intakeId, "document", kind],
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/buylist-intakes/{intake_id}/documents/{kind}", {
          params: { path: { intake_id: intakeId, kind } },
        }),
      ),
    staleTime: 60_000,
  });
  const openLink = () =>
    call(() =>
      api.POST("/v1/pos/buylist-intakes/{intake_id}/documents/{kind}/link", {
        params: { path: { intake_id: intakeId, kind } },
      }),
    );
  if (!state.data || state.data.status === "none") {
    return null;
  }
  return (
    <DocumentButton
      label={t(`document.${kind}`)}
      number={state.data.number ?? null}
      state={state.data.status}
      open={openLink}
    />
  );
}
